"use client";

export const runtime = "edge";

import { useEffect } from "react";
import Link from "next/link";
import { ArrowRight, Check, ChevronRight, Clock, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/hooks/useStore";
import { getPpdbList } from "@/store/controller/ppdb.controller";
import { getContactList } from "@/store/controller/contact.controller";
import { PPDB_CTA_DEFAULTS } from "@/app/components/landing/ppdbCta";

export default function PpdbPage() {
  const dispatch = useAppDispatch();
  const ppdb = useAppSelector((state) => state.ppdb.list[0]);
  const contact = useAppSelector((state) => state.contact.list[0]);

  useEffect(() => {
    dispatch(getPpdbList({}));
    dispatch(getContactList({}));
  }, [dispatch]);

  if (!ppdb) {
    return <div className="pt-32 pb-20 text-center text-gray-400">Informasi PPDB belum tersedia.</div>;
  }

  const whatsappHref = contact?.whatsapp ? `https://wa.me/${contact.whatsapp.replace(/\D/g, "")}` : null;

  const contactRows = [
    contact?.telephone ? { Icon: Phone, label: contact.telephone } : null,
    contact?.whatsapp ? { Icon: MessageCircle, label: `${contact.whatsapp} (WhatsApp)` } : null,
    contact?.email ? { Icon: Mail, label: contact.email } : null,
    contact?.location ? { Icon: MapPin, label: contact.location } : null,
  ].filter((row): row is { Icon: typeof Phone; label: string } => row !== null);

  return (
    <div className="pt-20">
      <div
        className="relative py-16 sm:py-24 px-4 overflow-hidden"
        style={{ background: "linear-gradient(135deg, #0d4d0d 0%, #1a7a1a 100%)" }}
      >
        <div className="relative max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-green-300 text-sm mb-6">
            <Link href="/" className="hover:text-white">
              Beranda
            </Link>
            <ChevronRight size={14} />
            <span className="text-white">PPDB</span>
          </div>
          <div className="max-w-2xl">
            <h1 className="text-3xl sm:text-5xl font-extrabold text-white mb-3 leading-tight">{ppdb.title}</h1>
            <p className="text-3xl font-bold text-yellow-400 mb-5">{ppdb.year}</p>
            <p className="text-green-200 text-base leading-relaxed max-w-lg mb-8">{ppdb.description}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="#daftar"
                className="inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-yellow-900 font-extrabold px-8 py-4 rounded-2xl transition-all text-base shadow-xl"
              >
                {ppdb.page_cta_label || PPDB_CTA_DEFAULTS.page} <ArrowRight size={20} />
              </a>
              {whatsappHref && (
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-white/15 hover:bg-white/25 border border-white/30 text-white font-semibold px-6 py-4 rounded-2xl transition-all text-base"
                >
                  <MessageCircle size={18} /> WhatsApp Kami
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {ppdb.timeline && ppdb.timeline.length > 0 && (
        <div className="bg-white border-b border-gray-100 py-6 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap gap-3 justify-center sm:justify-start text-sm">
              {ppdb.timeline.map((item, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl ${
                    item.highlight ? "bg-orange-100 border border-orange-300" : "bg-gray-50 border border-gray-200"
                  }`}
                >
                  <Clock size={13} className={item.highlight ? "text-orange-600" : "text-gray-400"} />
                  <span className={`font-medium ${item.highlight ? "text-orange-800" : "text-gray-600"}`}>{item.label}:</span>
                  <span className={`font-bold ${item.highlight ? "text-orange-700" : "text-gray-800"}`}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 space-y-16">
        {ppdb.steps && ppdb.steps.length > 0 && (
          <div>
            <div className="text-center mb-10">
              <span className="inline-block text-green-700 font-semibold text-sm uppercase tracking-widest mb-3">Prosedur</span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">Alur Pendaftaran</h2>
              <div className="w-14 h-1 bg-yellow-400 rounded mx-auto" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {ppdb.steps.map((step) => (
                <div key={step.step} className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 rounded-full bg-green-700 text-white font-extrabold flex items-center justify-center text-lg mb-4">
                    {step.step}
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {(ppdb.requirements?.length || ppdb.tuition?.length) ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {ppdb.requirements && ppdb.requirements.length > 0 && (
              <div>
                <h2 className="text-xl font-extrabold text-gray-900 mb-5">Persyaratan Dokumen</h2>
                <div className="space-y-2.5">
                  {ppdb.requirements.map((requirement, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-green-50 rounded-xl border border-green-100 text-sm text-green-900">
                      <Check size={14} className="text-green-600 mt-0.5 flex-shrink-0" strokeWidth={3} />
                      {requirement}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {ppdb.tuition && ppdb.tuition.length > 0 && (
              <div>
                <h2 className="text-xl font-extrabold text-gray-900 mb-5">Biaya Pendidikan</h2>
                <div className="space-y-3">
                  {ppdb.tuition.map((item, index) => (
                    <div key={index} className="p-4 bg-white border border-gray-100 rounded-2xl shadow-sm flex items-center justify-between gap-4">
                      <div>
                        <div className="font-semibold text-gray-900 text-sm">{item.label}</div>
                        <div className="text-gray-400 text-xs mt-0.5">{item.note}</div>
                      </div>
                      <div className="font-extrabold text-base text-gray-900">{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}

        <div
          id="daftar"
          className="rounded-3xl overflow-hidden"
          style={{ background: "linear-gradient(135deg, #0d4d0d, #1a7a1a)" }}
        >
          <div className="p-8 sm:p-12 flex flex-col lg:flex-row items-center gap-8">
            <div className="flex-1 text-center lg:text-left">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">Siap Mendaftar?</h2>
              <p className="text-green-200 leading-relaxed max-w-lg">
                Isi formulir pendaftaran sesuai jadwal di atas. Tim panitia PPDB siap membantu proses pendaftaran Anda.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
              {whatsappHref && (
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-yellow-900 font-extrabold px-8 py-4 rounded-2xl transition-all text-base shadow-xl"
                >
                  <MessageCircle size={20} /> Hubungi via WhatsApp
                </a>
              )}
            </div>
          </div>
        </div>

        {contactRows.length > 0 && (
          <div className="text-center">
            <h2 className="text-xl font-extrabold text-gray-900 mb-6">Kontak Panitia PPDB</h2>
            <div className="flex flex-wrap justify-center gap-4">
              {contactRows.map((row) => (
                <div key={row.label} className="flex items-center gap-2 px-5 py-3 bg-gray-50 rounded-xl border border-gray-100 text-sm text-gray-700">
                  <row.Icon size={16} className="text-green-600 flex-shrink-0" />
                  {row.label}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
